import { SubAPI } from './API'
import {
  ICombinedHydratedResult,
  ICombinedResultsRequestCandidate,
  ICombinedSearchOptions,
  ICombinedSearchResponse,
} from 'types/searchInterfaces'

/**
 * Client for caleo-back's combined search service (`/search`, `/search/results`, see caleo-back
 * TODO-0036). Runs structured search first and, when free text is supplied, scores/reorders those
 * same candidates with the vector search - a single combined operation replacing the separate
 * `persons/search`/`cvs/search` calls.
 */
class CombinedSearchSubAPI extends SubAPI {
  /**
   * Runs the combined search - structured tokens first, optionally scored/reordered by free text.
   *
   * @param data - Search options.
   * @param controller - Optional AbortController.
   * @returns Capped list of scored candidates (no Person data - see postResults) plus the
   *   AI-improved query, if query improvement ran and changed it.
   */
  public async search(data: ICombinedSearchOptions, controller?: AbortController): Promise<ICombinedSearchResponse> {
    return this.api.post(`search`, data, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Lazily hydrates a page of candidates (from `search`) into full Person data. The backend answers
   * 403 for named ids the user can't see with names, and drops handles that have expired.
   *
   * @param candidates - The exact page of candidates to hydrate.
   * @param controller - Optional AbortController.
   * @returns Hydrated results, in the same order as the input candidates.
   */
  public async postResults(
    candidates: ICombinedResultsRequestCandidate[],
    controller?: AbortController
  ): Promise<ICombinedHydratedResult[]> {
    return this.api.post(`search/results`, { candidates }, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Proposes an assignment to the person behind an anonymous combined-search result.
   *
   * @param anonymousId - The anonymous candidate's handle from `search`, identifying the person.
   * @param data - Assignment details entered in the propose modal.
   * @param controller - Optional AbortController.
   */
  public async propose(anonymousId: string, data: unknown, controller?: AbortController): Promise<void> {
    return this.api.post(`search/propose/${anonymousId}`, data, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Requests a shortlist be compiled for a non-anonymous combined-search result.
   *
   * @param data - Assignment/shortlist request details entered in the propose modal.
   * @param controller - Optional AbortController.
   */
  public async request(data: unknown, controller?: AbortController): Promise<void> {
    return this.api.post(`search/request`, data, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Starts re-indexing the CVs changed since they were last indexed: their chunks and embeddings are
   * regenerated, and the HNSW vector index picks up the new rows as they're written. Runs in the
   * background on the backend, which responds 202 straight away.
   */
  public async triggerRebuildIndex(): Promise<void> {
    return this.api.get(`search/rebuild-index`)
  }
}

export const combinedSearchApi = new CombinedSearchSubAPI()
